<script setup>
import { computed, onMounted, ref } from 'vue';
import { endOfDay, getUnixTime, startOfDay, subDays } from 'date-fns';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import ReportHeader from './components/ReportHeader.vue';
import SearchDateRangeSelector from 'dashboard/modules/search/components/SearchDateRangeSelector.vue';
import SearchInboxSelector from 'dashboard/modules/search/components/SearchInboxSelector.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useStore } from 'dashboard/composables/store';

useI18n();
const store = useStore();

const filters = ref({
  inboxId: null,
  dateRange: {
    type: 'last_30_days',
    from: getUnixTime(startOfDay(subDays(new Date(), 30))),
    to: getUnixTime(endOfDay(new Date())),
  },
});

const isLoading = ref(false);
const isExporting = ref(false);
const report = ref({
  totalCount: 0,
  categories: [],
  types: [],
  combinations: [],
});

const totalCount = computed(() => report.value.totalCount || 0);

const withPercent = rows =>
  rows.map(row => ({
    ...row,
    percentage:
      totalCount.value > 0
        ? ((row.count / totalCount.value) * 100).toFixed(1)
        : '0.0',
  }));

const categoryRows = computed(() => withPercent(report.value.categories || []));
const typeRows = computed(() => withPercent(report.value.types || []));
const combinationRows = computed(() =>
  withPercent(report.value.combinations || [])
);

const hasData = computed(() => totalCount.value > 0);

const payload = computed(() => ({
  from: filters.value.dateRange?.from,
  to: filters.value.dateRange?.to,
  inboxId: filters.value.inboxId,
}));

const fetchReport = async () => {
  try {
    isLoading.value = true;
    const { data } = await ReportsAPI.getClosingSummary(payload.value);
    report.value = {
      totalCount: data.total_count || 0,
      categories: data.categories || [],
      types: data.types || [],
      combinations: data.combinations || [],
    };
  } finally {
    isLoading.value = false;
  }
};

const exportReport = async () => {
  try {
    isExporting.value = true;
    const { data } = await ReportsAPI.downloadClosingSummary(payload.value);
    const timestamp = filters.value.dateRange?.to || getUnixTime(new Date());
    const fileName = `closing-summary-report-${timestamp}.csv`;
    downloadCsvFile(fileName, data);
  } finally {
    isExporting.value = false;
  }
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  await fetchReport();
});
</script>

<template>
  <div class="flex flex-col gap-5 pb-8">
    <ReportHeader
      :header-title="$t('CLOSING_REPORTS.HEADER')"
      :header-description="$t('CLOSING_REPORTS.DESCRIPTION')"
    >
      <NextButton
        slate
        :label="$t('CLOSING_REPORTS.DOWNLOAD')"
        icon="i-lucide-download"
        :is-loading="isExporting"
        @click="exportReport"
      />
    </ReportHeader>

    <div
      class="chatmigo-report-filter-card rounded-xl border border-n-weak bg-n-alpha-1 p-4"
    >
      <div class="flex flex-col lg:flex-row items-start lg:items-center gap-3">
        <SearchDateRangeSelector
          v-model="filters.dateRange"
          class="min-w-0 max-w-full"
          @change="fetchReport"
        />
        <div class="w-px h-4 bg-n-weak flex-shrink-0 hidden lg:block" />
        <SearchInboxSelector
          v-model="filters.inboxId"
          :label="$t('CLOSING_REPORTS.INBOX_FILTER')"
          @change="fetchReport"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div
        class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <p class="mb-1 text-sm text-n-slate-11">
          {{ $t('CLOSING_REPORTS.TOTAL_CLOSED') }}
        </p>
        <p class="mb-0 text-2xl font-semibold text-n-slate-12">
          {{ totalCount }}
        </p>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="chatmigo-report-empty-state flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <p class="m-0 text-n-slate-11">
        {{ $t('CLOSING_REPORTS.LOADING') }}
      </p>
    </div>

    <div
      v-else-if="!hasData"
      class="chatmigo-report-empty-state flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <p class="m-0 text-n-slate-11">
        {{ $t('CLOSING_REPORTS.EMPTY_STATE') }}
      </p>
    </div>

    <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-5">
      <section
        class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <h3 class="mb-4 text-base font-semibold text-n-slate-12">
          {{ $t('CLOSING_REPORTS.BY_CATEGORY') }}
        </h3>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-11 border-b border-n-weak">
                <th class="py-2 pr-3">{{ $t('CLOSING_REPORTS.CATEGORY') }}</th>
                <th class="py-2 pr-3">{{ $t('CLOSING_REPORTS.COUNT') }}</th>
                <th class="py-2">{{ $t('CLOSING_REPORTS.PERCENTAGE') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in categoryRows"
                :key="`category-${row.label}`"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="py-2 pr-3 text-n-slate-12">{{ row.label }}</td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.count }}</td>
                <td class="py-2 text-n-slate-11">{{ row.percentage }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <h3 class="mb-4 text-base font-semibold text-n-slate-12">
          {{ $t('CLOSING_REPORTS.BY_TYPE') }}
        </h3>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-11 border-b border-n-weak">
                <th class="py-2 pr-3">{{ $t('CLOSING_REPORTS.TYPE') }}</th>
                <th class="py-2 pr-3">{{ $t('CLOSING_REPORTS.COUNT') }}</th>
                <th class="py-2">{{ $t('CLOSING_REPORTS.PERCENTAGE') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in typeRows"
                :key="`type-${row.label}`"
                class="border-b border-n-weak last:border-b-0"
              >
                <td class="py-2 pr-3 text-n-slate-12">{{ row.label }}</td>
                <td class="py-2 pr-3 text-n-slate-12">{{ row.count }}</td>
                <td class="py-2 text-n-slate-11">{{ row.percentage }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <section
      v-if="hasData"
      class="chatmigo-report-card rounded-xl border border-n-weak bg-n-solid-1 p-4"
    >
      <h3 class="mb-4 text-base font-semibold text-n-slate-12">
        {{ $t('CLOSING_REPORTS.BY_COMBINATION') }}
      </h3>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11 border-b border-n-weak">
              <th class="py-2 pr-3">{{ $t('CLOSING_REPORTS.CATEGORY') }}</th>
              <th class="py-2 pr-3">{{ $t('CLOSING_REPORTS.TYPE') }}</th>
              <th class="py-2 pr-3">{{ $t('CLOSING_REPORTS.COUNT') }}</th>
              <th class="py-2">{{ $t('CLOSING_REPORTS.PERCENTAGE') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in combinationRows"
              :key="`combination-${row.category}-${row.type}`"
              class="border-b border-n-weak last:border-b-0"
            >
              <td class="py-2 pr-3 text-n-slate-12">{{ row.category }}</td>
              <td class="py-2 pr-3 text-n-slate-12">{{ row.type }}</td>
              <td class="py-2 pr-3 text-n-slate-12">{{ row.count }}</td>
              <td class="py-2 text-n-slate-11">{{ row.percentage }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>
